import { nextTick, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type { NewRirekishoFormatData } from '@/composables/format-edit/useNewRirekishoFormatData'

type AiAxiosVm = {
  $axios: {
    post: (
      url: string,
      data: FormData,
      config?: { timeout: number }
    ) => Promise<{ data: { success?: boolean; message?: string } }>
  }
  $yagiprimePaymentService: {
    init?: (vm: unknown) => void
    cleanup?: (vm: unknown) => void
    useFeature: (
      vm: unknown,
      feature: string,
      hooks: {
        onSuccess: () => void
        onAlreadyPremium?: () => void
        onCancel: () => void
      }
    ) => void
  }
}

function errorMessage(error: unknown): string | undefined {
  const err = error as { response?: { data?: { message?: string } } }
  return err.response && err.response.data && err.response.data.message
}

export function useNewRirekishoAiMypr(formatData: NewRirekishoFormatData) {
  const { vm, message, varuna } = useHostServices()
  const host = vm as unknown as AiAxiosVm
  const { user, isAuthenticated } = useAuth()

  const fullscreenLoading = ref(false)
  const aiResultView = ref(false)
  const aiCampusHistoryResult = ref('')
  const aiGakuchikaResult = ref('')

  function appendUuid(body: FormData) {
    if (isAuthenticated.value && user.value?.uid) {
      body.append('uuid', user.value.uid)
    }
  }

  function getAIMyprNew() {
    fullscreenLoading.value = true
    nextTick(() => {
      const formDataCampus = new FormData()
      appendUuid(formDataCampus)
      formDataCampus.append(
        'content',
        String(formatData.campus_history_content || '')
      )
      formDataCampus.append(
        'summary',
        String(formatData.campus_history_summary || '')
      )

      const campusPromise = host.$axios
        .post(varuna.endPoints.aiIntroduceCampusHistory, formDataCampus, {
          timeout: 60000
        })
        .then((response) => {
          if (response.data.success) {
            aiCampusHistoryResult.value =
              '<得意な科目または研究課題・ゼミ・卒論など><br>' +
              response.data.message
            aiResultView.value = true
            message({ message: '取得しました', type: 'success' })
          } else {
            message({ message: '失敗しました', type: 'error' })
          }
        })
        .catch((error: unknown) => {
          message({
            message: errorMessage(error) || '失敗しました',
            type: 'error'
          })
        })

      const formDataGakuchika = new FormData()
      appendUuid(formDataGakuchika)
      formDataGakuchika.append(
        'content',
        String(formatData.gakuchika_content || '')
      )
      formDataGakuchika.append(
        'summary',
        String(formatData.gakuchika_summary || '')
      )

      const gakuchikaPromise = host.$axios
        .post(varuna.endPoints.aiIntroduceGakuchika, formDataGakuchika, {
          timeout: 60000
        })
        .then((response) => {
          if (response.data.success) {
            aiGakuchikaResult.value =
              '<学生生活で力を注いだこと><br>' + response.data.message
            aiResultView.value = true
            message({ message: '取得しました', type: 'success' })
          } else {
            message({ message: '失敗しました', type: 'error' })
          }
        })
        .catch((error: unknown) => {
          message({
            message: errorMessage(error) || '失敗しました',
            type: 'error'
          })
        })

      Promise.all([campusPromise, gakuchikaPromise]).finally(() => {
        fullscreenLoading.value = false
      })
    })
  }

  function getAIMyprNewContinue() {
    fullscreenLoading.value = true
    const body = new FormData()
    appendUuid(body)
    body.append(
      'data',
      aiCampusHistoryResult.value + '\n\n' + aiGakuchikaResult.value
    )

    host.$axios
      .post(varuna.endPoints.aiSentenseContinue, body, { timeout: 60000 })
      .then((response) => {
        if (response.data.success) {
          const messages = String(response.data.message || '').split('\n\n')
          aiCampusHistoryResult.value = messages[0]
          aiGakuchikaResult.value = messages[1]
          aiResultView.value = true
          message({ message: '取得しました', type: 'success' })
        } else {
          message({ message: '失敗しました', type: 'error' })
        }
      })
      .catch((error: unknown) => {
        message({
          message: errorMessage(error) || '失敗しました',
          type: 'error'
        })
      })
      .finally(() => {
        fullscreenLoading.value = false
      })
  }

  function viewPaymentYagiprime() {
    host.$yagiprimePaymentService.useFeature(vm, 'self-mypr-new-rirekisho', {
      onSuccess: () => {
        message({
          message:
            'ヤギプラ加入に成功しました。5秒ほどお待ちいただくと、全文が適用されます。',
          type: 'success'
        })
        getAIMyprNewContinue()
      },
      onAlreadyPremium: () => {
        aiResultView.value = true
        getAIMyprNewContinue()
      },
      onCancel: () => {
        fullscreenLoading.value = false
      }
    })
  }

  function applyGeneratedPr() {
    formatData.reasonNew =
      aiCampusHistoryResult.value.replace(/<br>/gi, '\n') +
      '\n' +
      '\n' +
      aiGakuchikaResult.value.replace(/<br>/gi, '\n') +
      '\n'
  }

  onBeforeMount(() => {
    if (typeof host.$yagiprimePaymentService.init === 'function') {
      host.$yagiprimePaymentService.init(vm)
    }
  })

  onBeforeUnmount(() => {
    if (typeof host.$yagiprimePaymentService.cleanup === 'function') {
      host.$yagiprimePaymentService.cleanup(vm)
    }
  })

  return {
    fullscreenLoading,
    aiResultView,
    aiCampusHistoryResult,
    aiGakuchikaResult,
    getAIMyprNew,
    viewPaymentYagiprime,
    applyGeneratedPr
  }
}
